import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  getAllNoiseLayerIds,
  createNoiseFillColorExpression,
  NOISE_LAYER_ORDER,
  getNoiseField,
  formatNoiseValue,
  getNoiseLayerDisplayName,
  getNoiseIndex,
  createNoiseVectorTileSource,
} from '../../../../molecules/AnalysisMap/utils/noiseLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'
import { API_BASE_URL } from '../../../../../config'

interface UseNoiseLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

/**
 * Hook to handle noise choropleth layers (H3 hexagon vector tiles) and hover tooltips
 */
export const useNoiseLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseNoiseLayersProps) => {
  // Handle noise choropleth layers (H3 hexagon vector tiles)
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // Check if noise section is active
    const activeSection = (otherMapOptions as any).activeSection
    const isNoiseSection =
      activeSection === 'noise' || activeSection === 'noise_analysis'
    const noiseLayerStates = (otherMapOptions as any).demographicsLayerStates // Reuse same prop for consistency

    if (!isNoiseSection || !noiseLayerStates) {
      // Remove noise sources and layers if not in noise section
      let existingNoiseLayers: string[] = []
      try {
        const style = map.current.getStyle()
        if (style?.layers) {
          existingNoiseLayers = style.layers
            .filter((layer) => layer.id.startsWith('noise-'))
            .map((layer) => layer.id)
        }
      } catch (error) {
        // Style not loaded yet, skip cleanup
        return
      }

      existingNoiseLayers.forEach((layerId) => {
        if (map.current!.getLayer(layerId)) {
          try {
            map.current!.removeLayer(layerId)
          } catch (error) {
            console.warn(`Failed to remove noise layer ${layerId}:`, error)
          }
        }
      })

      // Remove all noise vector tile sources (one per layer)
      getAllNoiseLayerIds().forEach((layerId) => {
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          try {
            map.current!.removeSource(sourceId)
          } catch (error) {
            console.warn(`Failed to remove noise source ${sourceId}:`, error)
          }
        }
      })

      return
    }

    // Noise section is active - add vector tile sources and layers
    // Like pricing layers, noise layers each need their own source
    // because they query different Elasticsearch indices
    const layerIds = getAllNoiseLayerIds()

    layerIds.forEach((layerId: string) => {
      const layerConfig = getLayerConfig(layerId)
      const layerState = noiseLayerStates[layerId] || {
        enabled: layerConfig?.defaultEnabled || false,
        opacity: layerConfig?.defaultOpacity || 30,
      }

      const index = getNoiseIndex(layerId)
      if (!index) {
        console.warn(`No index mapping found for noise layer: ${layerId}`)
        return
      }

      const sourceId = `${layerId}-source`

      // Add vector tile source if it doesn't exist (one per layer)
      if (!map.current!.getSource(sourceId)) {
        const sourceConfig = createNoiseVectorTileSource(API_BASE_URL, index)
        map.current!.addSource(sourceId, sourceConfig)
      }

      if (!map.current!.getLayer(layerId)) {
        // Add new fill layer

        const fillColorExpression = createNoiseFillColorExpression(
          layerId,
          layerConfig
        )

        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId, // Use layer-specific source
          'source-layer': 'aggs', // H3 hexagon aggregations layer from Elasticsearch searchMvt
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'fill-outline-color': 'transparent',
            'fill-color': fillColorExpression,
            'fill-opacity': layerState.opacity / 100,
          },
        })
      } else {
        // Update existing layer
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          layerId,
          'fill-opacity',
          layerState.opacity / 100
        )
      }
    })

    // Apply Z-ordering to ensure proper layer stacking
    NOISE_LAYER_ORDER.forEach((layerId: string, index: number) => {
      if (map.current!.getLayer(layerId) && index > 0) {
        const previousLayerId = NOISE_LAYER_ORDER[index - 1]
        if (map.current!.getLayer(previousLayerId)) {
          try {
            map.current!.moveLayer(layerId, previousLayerId)
          } catch (error) {
            // Layer ordering might fail if layers don't exist yet, that's ok
          }
        }
      }
    })
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Setup hover tooltips for noise layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Get all enabled noise layers
    const enabledNoiseLayers = getAllNoiseLayerIds().filter((layerId) => {
      const layerState = otherMapOptions.demographicsLayerStates?.[layerId]
      return layerState?.enabled && map.current!.getLayer(layerId)
    })

    if (enabledNoiseLayers.length === 0) return

    // Find the topmost visible layer (based on Z-order)
    const topmostLayer = NOISE_LAYER_ORDER.slice()
      .reverse()
      .find((layerId) => enabledNoiseLayers.includes(layerId))

    if (!topmostLayer) return

    const field = getNoiseField(topmostLayer)
    if (!field) return

    // Handle mouse move to show tooltip
    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: [topmostLayer],
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const value = feature.properties?.[field]
        const formattedValue = formatNoiseValue(topmostLayer, value)
        const layerName = getNoiseLayerDisplayName(topmostLayer)

        // Change cursor to pointer
        map.current!.getCanvas().style.cursor = 'pointer'

        // Show popup with formatted value
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="padding: 8px 12px; font-size: 13px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${layerName}</div>
              <div>${formattedValue}</div>
            </div>`
          )
          .addTo(map.current!)
      } else {
        // Reset cursor
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    // Handle mouse leave to hide tooltip
    const handleMouseLeave = () => {
      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    // Add event listeners
    map.current.on('mousemove', topmostLayer, handleMouseMove)
    map.current.on('mouseleave', topmostLayer, handleMouseLeave)

    // Cleanup
    return () => {
      if (map.current) {
        map.current.off('mousemove', topmostLayer, handleMouseMove)
        map.current.off('mouseleave', topmostLayer, handleMouseLeave)
      }
      popup.current?.remove()
    }
  }, [mapLoaded, otherMapOptions.demographicsLayerStates])
}
